/**
 * Rendering singolo blocco "Controllo completo" — varianti per `size`:
 * kpi | percentage | details | list | bar | quick-actions
 */
import { Link } from "react-router-dom";
import {
  Bed,
  Users,
  Home as HomeIcon,
  TrendingUp,
  Receipt,
  FileText,
  Package,
  ChevronRight,
  ShoppingCart,
  Upload,
  Bell,
  BarChart3,
  UserPlus,
  AlertTriangle,
  ShieldCheck,
  BookOpen,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

const DIARIO_TURNO_RIGHE = [
  { key: "mattina", label: "Mattina", fascia: "Fino alle 14:00" },
  { key: "pomeriggio", label: "Pomeriggio", fascia: "Fino alle 22:00" },
  {
    key: "notte",
    label: "Notte",
    fascia: "Fino alle 06:00 (giorno dopo)",
  },
];

const KPI_BLOCK_TO_KEY = {
  "kpi-posti-totali": "posti-totali",
  "kpi-ospiti-presenti": "ospiti-presenti",
  "kpi-residenti": "ospiti-residenti",
  "kpi-camere": "posti-liberi",
  "kpi-ricavi-mese": "ricavi-mese",
  "kpi-spese-mese": "spese-mese",
  "kpi-margine-operativo": "margine-operativo",
  "kpi-fatture-non-pagate": "fatture-non-pagate",
  "kpi-piani-scadenza": "fascicoli-alert",
  "kpi-alloggiati-web": "alloggiati-reinvio",
};

function resolveKpiFromCards(kpiCards, blockId) {
  const mapKey = KPI_BLOCK_TO_KEY[blockId];
  if (!mapKey) return null;
  return kpiCards?.find((k) => k.key === mapKey) ?? null;
}

/** Percentuale 0–100 per visualizzazioni grafiche (percentuale / barra KPI). */
function kpiPctForChart(blockId, m) {
  if (!m) return 0;
  const div = (a, b) => (b > 0 ? Math.min(100, Math.max(0, Math.round((Number(a) / Number(b)) * 100))) : 0);
  switch (blockId) {
    case "kpi-posti-totali":
      return m.occupatePerc ?? 0;
    case "kpi-ospiti-presenti":
      return div(m.ospitiPresenti, m.postiTotali);
    case "kpi-residenti":
      return div(m.ospitiResidenti, Math.max(m.fascicoliAttiviCount, 1));
    case "kpi-camere":
      return div(m.postiLiberi, m.postiTotali);
    case "kpi-ricavi-mese": {
      const a = Number(m.ricaviMese) || 0;
      const b = Number(m.ricaviMesePrecedente) || 0;
      const tot = a + b;
      return tot > 0 ? Math.round((a / tot) * 100) : 0;
    }
    case "kpi-spese-mese":
      return div(m.speseMese, Math.max(m.ricaviMese, 1));
    case "kpi-margine-operativo": {
      const r = Number(m.ricaviMese) || 0;
      const mg = Number(m.margineOperativo) || 0;
      return r > 0 ? Math.min(100, Math.max(0, Math.round((mg / r) * 100))) : 0;
    }
    case "kpi-fatture-non-pagate":
      return div(m.fattureNonPagateCount, Math.max(m.fattureTotCount, 1));
    case "kpi-piani-scadenza":
      return m.alertPTCount > 0 ? Math.min(100, 20 + m.alertPTCount * 22) : 0;
    case "kpi-alloggiati-web":
      return m.alloggiatiAlertTot > 0 ? Math.min(100, 30 + m.alloggiatiAlertTot * 18) : 0;
    default:
      return 0;
  }
}

function donutDataFromPct(p) {
  const fill = Math.max(0, Math.min(100, p));
  if (fill <= 0) {
    return [
      { name: "Vuoto", value: 99, color: "#E2E8F0" },
      { name: "Voce", value: 1, color: "#10B981" },
    ];
  }
  const rest = Math.max(2, 100 - fill);
  return [
    { name: "Indicatore", value: Math.max(fill, 1), color: "#10B981" },
    { name: "Altro", value: rest, color: "#E2E8F0" },
  ];
}

/** Righe sintetiche per la vista lista. */
function kpiExpandListRows(blockId, kpi, m, postiLiberiInfo) {
  if (!kpi) return [];
  const rows = [{ title: String(kpi.value), subtitle: kpi.label }];
  rows.push({ title: kpi.hint, subtitle: "Andamento / contesto" });
  switch (blockId) {
    case "kpi-posti-totali":
      rows.push({
        title: `${m?.postiOccupati ?? 0} occupati`,
        subtitle: `${m?.postiLiberi ?? 0} posti liberi su ${m?.postiTotali ?? 0}`,
      });
      break;
    case "kpi-ospiti-presenti":
      rows.push({
        title: `${(m?.deltaOspitiMese ?? 0) >= 0 ? "+" : ""}${m?.deltaOspitiMese ?? 0} questo mese`,
        subtitle: "Saldo ingressi − uscite (mese corrente)",
      });
      break;
    case "kpi-residenti":
      rows.push({
        title: `${m?.fascicoliAttiviCount ?? 0} fascicoli attivi`,
        subtitle: "Quota residenti sulla popolazione in struttura",
      });
      break;
    case "kpi-camere":
      rows.push({
        title:
          typeof postiLiberiInfo?.totale_camere === "number"
            ? `${postiLiberiInfo.totale_camere} camere dichiarate`
            : "Vedi modulo posti liberi",
        subtitle: `${m?.postiLiberi ?? 0} posti disponibili`,
      });
      break;
    case "kpi-ricavi-mese":
      rows.push({
        title: m?.ospitiConteggio != null
          ? `${m.ospitiConteggio} ospiti attivi contribuiscono`
          : "Rette ospiti attivi",
        subtitle: "Somma rette/tariffe mensili previste",
      });
      break;
    case "kpi-spese-mese":
      rows.push({
        title: `€ ${Number(m?.speseMese ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
        subtitle: "Prima nota spese + fatture passive del mese",
      });
      break;
    case "kpi-margine-operativo":
      rows.push({
        title: `€ ${Number(m?.margineOperativo ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
        subtitle: Number(m?.ricaviMese ?? 0) > 0
          ? `${Math.round((Number(m.margineOperativo ?? 0) / Number(m.ricaviMese)) * 100)}% dei ricavi`
          : "Ricavi - Spese del mese",
      });
      break;
    case "kpi-fatture-non-pagate":
      rows.push({
        title: `€ ${(m?.totaleNonPagato ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`,
        subtitle: `${m?.fattureTotCount ?? 0} fatture in archivio · ${m?.fattureNonPagateCount ?? 0} con residuo`,
      });
      break;
    case "kpi-piani-scadenza":
      rows.push({
        title: `${m?.alertPTCount ?? 0} piani/documenti nel periodo di monitoraggio`,
        subtitle: "Piani assistenziali in scadenza (30 giorni)",
      });
      break;
    case "kpi-alloggiati-web":
      rows.push({
        title: `${Number(m?.alertReinvioScaduti ?? 0)} scaduti · ${Number(m?.alertReinvioInScadenza ?? 0)} in scadenza 48h`,
        subtitle: "Ospiti con invii da gestire",
      });
      break;
    default:
      break;
  }
  return rows.slice(0, 5);
}

function kpiPctFooterLabel(blockId) {
  switch (blockId) {
    case "kpi-posti-totali":
      return "% posti occupati";
    case "kpi-ospiti-presenti":
      return "% su posti totali";
    case "kpi-residenti":
      return "% residenti su ospiti";
    case "kpi-camere":
      return "% posti liberi";
    case "kpi-ricavi-mese":
      return "Quota su mese corrente + precedente";
    case "kpi-spese-mese":
      return "% spese su ricavi previsti";
    case "kpi-margine-operativo":
      return "% margine su ricavi";
    case "kpi-fatture-non-pagate":
      return "% fatture con residuo";
    default:
      return "Indice";
  }
}

function KpiPctChartCard({ blockId, kpi, m }) {
  const pct = kpiPctForChart(blockId, m);
  const chart = donutDataFromPct(pct);
  const IconComp = kpi?.icon ?? Bed;
  if (!kpi) {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm opacity-70">
        <CardContent className="p-5 flex items-center justify-center min-h-[200px]">
          <span className="text-sm text-slate-400">—</span>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-11 h-11 rounded-xl ${kpi.iconBg} flex items-center justify-center`}>
            <IconComp className={`w-5 h-5 ${kpi.iconColor}`} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-slate-500">{kpi.label}</p>
            <p className="text-xl font-bold text-slate-900 truncate">{kpi.value}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative w-32 h-32 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chart}
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={54}
                  paddingAngle={chart.length > 1 ? 2 : 0}
                  cornerRadius={6}
                  dataKey="value"
                  stroke="none"
                >
                  {chart.map((entry, idx) => (
                    <Cell key={`c-${idx}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-emerald-600 tabular-nums leading-none">{pct}%</span>
            </div>
          </div>
          <div className="flex-1 min-w-0 space-y-1.5 text-[11px] text-slate-600">
            <p className="font-semibold text-slate-800">{kpiPctFooterLabel(blockId)}</p>
            <p className="text-slate-500 leading-snug">{kpi.hint}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiDetailChartCard({ blockId, kpi, m, postiLiberiInfo }) {
  const IconComp = kpi?.icon ?? Bed;
  const extraLines = [];
  if (m && blockId === "kpi-posti-totali") {
    extraLines.push(
      `Capacità: ${m.postiTotali ?? 0} posti · Occupati ${m.postiOccupati ?? 0} · Liberi ${m.postiLiberi ?? 0}`
    );
  }
  if (m && blockId === "kpi-ospiti-presenti") {
    extraLines.push(`Ospiti in struttura: ${m.ospitiPresenti ?? "—"} (variazione mese → ${m.deltaOspitiMese ?? 0})`);
  }
  if (m && blockId === "kpi-residenti") {
    extraLines.push(
      `${m.ospitiResidenti ?? 0} residenti su ${Math.max(m.fascicoliAttiviCount ?? 0, 1)} fascicoli attivi`
    );
  }
  if (m && blockId === "kpi-camere" && typeof postiLiberiInfo?.totale_camere === "number") {
    extraLines.push(`${postiLiberiInfo.totale_camere} camere dichiarate in anagrafica posti liberi`);
  }
  if (m && blockId === "kpi-ricavi-mese") {
    extraLines.push(
      m.ospitiConteggio != null
        ? `${m.ospitiConteggio} ospiti attivi · rette/tariffe previste del mese`
        : "Somma rette previste ospiti attivi"
    );
  }
  if (m && blockId === "kpi-spese-mese") {
    extraLines.push(`Prima nota spese + fatture passive del mese corrente`);
  }
  if (m && blockId === "kpi-margine-operativo") {
    extraLines.push(
      `Ricavi ${Number(m.ricaviMese ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} · Spese ${Number(m.speseMese ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    );
  }
  if (m && blockId === "kpi-fatture-non-pagate") {
    extraLines.push(
      `Residuo complessivo: € ${Number(m.totaleNonPagato ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
    );
  }
  if (!kpi) {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm opacity-70">
        <CardContent className="p-5 min-h-[200px] flex items-center justify-center">
          <span className="text-slate-400 text-sm">—</span>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl ${kpi.iconBg} flex items-center justify-center flex-shrink-0`}>
            <IconComp className={`w-5 h-5 ${kpi.iconColor}`} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Metrica</p>
            <h3 className="text-lg font-bold text-slate-900">{kpi.label}</h3>
          </div>
        </div>
        <div>
          <p className="text-3xl font-bold text-slate-900 tracking-tight tabular-nums">{kpi.value}</p>
          <p
            className={`text-sm mt-1 font-medium ${
              kpi.hintTone === "positive"
                ? "text-emerald-600"
                : kpi.hintTone === "negative"
                ? "text-rose-600"
                : "text-slate-500"
            }`}
          >
            {kpi.hint}
          </p>
        </div>
        {extraLines.length > 0 && (
          <div className="pt-3 mt-2 border-t border-slate-100 space-y-1.5 text-[13px] text-slate-600 leading-snug">
            {extraLines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KpiListChartCard({ blockId, kpi, m, postiLiberiInfo }) {
  const rows = kpiExpandListRows(blockId, kpi, m, postiLiberiInfo);
  if (!kpi || rows.length === 0) {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm opacity-70">
        <CardContent className="p-5 min-h-[200px] flex items-center justify-center">
          <span className="text-slate-400 text-sm">—</span>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <h3 className="text-base font-bold text-slate-900 mb-4">{kpi.label}</h3>
        <ul className="space-y-2.5">
          {rows.map((row, i) => (
            <li key={i} className="flex gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/60 ring-1 ring-slate-100/80">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 leading-snug break-words">{row.title}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{row.subtitle}</p>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function KpiBarChartCard({ blockId, kpi, m }) {
  const pct = kpiPctForChart(blockId, m);
  if (!kpi) {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm opacity-70">
        <CardContent className="p-5 min-h-[120px]" />
      </Card>
    );
  }
  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-full ${kpi.iconBg} flex items-center justify-center`}>
            <kpi.icon className={`w-5 h-5 ${kpi.iconColor}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] text-slate-500 font-medium">{kpi.label}</p>
            <p className="text-xl font-bold text-slate-900">{kpi.value}</p>
          </div>
          <span className="text-sm font-bold text-emerald-600 tabular-nums">{pct}%</span>
        </div>
        <p className={`text-[11px] mb-2 font-medium ${
          kpi.hintTone === "positive" ? "text-emerald-600" : kpi.hintTone === "negative" ? "text-rose-600" : "text-slate-400"
        }`}>
          {kpi.hint}
        </p>
        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[10px] text-slate-400 mt-2">{kpiPctFooterLabel(blockId)}</p>
      </CardContent>
    </Card>
  );
}

function KpiVariants({ blockId, size, cardData }) {
  const m = cardData?.kpiMetrics || {};
  const kpi = resolveKpiFromCards(cardData?.kpiCards, blockId);
  const pi = cardData?.postiLiberiInfo;

  if (size === "percentage") return <KpiPctChartCard blockId={blockId} kpi={kpi} m={m} />;
  if (size === "details") return <KpiDetailChartCard blockId={blockId} kpi={kpi} m={m} postiLiberiInfo={pi} />;
  if (size === "list") return <KpiListChartCard blockId={blockId} kpi={kpi} m={m} postiLiberiInfo={pi} />;
  if (size === "bar") return <KpiBarChartCard blockId={blockId} kpi={kpi} m={m} />;
  return <RenderKpiCard kpi={kpi} testId={`adv-kpi-${kpi?.key || blockId}`} />;
}


function RenderKpiCard({ kpi, testId }) {
  if (!kpi) {
    return (
      <Card className="card-base w-full bg-white border border-slate-200/70 shadow-sm opacity-70">
        <CardContent className="p-2 flex items-center justify-center min-h-[96px]">
          <span className="text-[11px] text-slate-400">—</span>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card
      className="card-base w-full bg-white border border-slate-200/70 shadow-sm hover:shadow-md transition-shadow"
      data-testid={testId || `adv-kpi-${kpi.key}`}
    >
      <CardContent className="p-2 pb-2.5">
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-full ${kpi.iconBg} flex items-center justify-center flex-shrink-0`}
          >
            <kpi.icon className={`w-5 h-5 ${kpi.iconColor}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium text-slate-500 leading-tight">{kpi.label}</p>
            <p className="text-2xl font-bold text-slate-900 leading-tight mt-0.5">{kpi.value}</p>
            <p
              className={`text-[11px] mt-0.5 truncate font-medium ${
                kpi.hintTone === "positive"
                  ? "text-emerald-600"
                  : kpi.hintTone === "negative"
                  ? "text-rose-600"
                  : "text-slate-400"
              }`}
            >
              {kpi.hint}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DiarioVariants({ size, diarioOggi }) {
  const header = (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-start gap-2 min-w-0">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 ring-1 ring-blue-100">
          <BookOpen className="h-5 w-5 text-blue-600" strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold text-slate-900 leading-tight">Diario consegne</h3>
          <p className="text-[11px] text-slate-500 mt-1">
            Oggi
            {diarioOggi.data
              ? ` · ${new Date(diarioOggi.data + "T12:00:00").toLocaleDateString("it-IT", {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}`
              : ""}{" "}
            (Europe/Rome) ·{" "}
            <span className="font-semibold text-slate-700 tabular-nums">{diarioOggi.consegne}</span>{" "}
            {diarioOggi.consegne === 1 ? "consegna" : "consegne"},{" "}
            <span className="font-semibold text-slate-700 tabular-nums">{diarioOggi.segnalazioni}</span>{" "}
            {diarioOggi.segnalazioni === 1 ? "segnalazione" : "segnalazioni"} totali
          </p>
        </div>
      </div>
      <Link
        to="/diario-consegne"
        className="text-[11px] font-semibold text-blue-700 hover:text-blue-800 whitespace-nowrap shrink-0 inline-flex items-center gap-0.5"
      >
        Modulo
        <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );

  if (size === "kpi") {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm" data-testid="adv-card-diario-oggi">
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-slate-500">Diario consegne · oggi</p>
              <p className="text-lg font-bold text-slate-900">
                {diarioOggi.consegne} consegne · {diarioOggi.segnalazioni} segnalazioni
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (size === "list") {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm" data-testid="adv-card-diario-oggi">
        <CardContent className="p-5">{header}</CardContent>
        <CardContent className="px-5 pb-5 pt-0 space-y-2">
          {DIARIO_TURNO_RIGHE.map((row) => {
            const stats = diarioOggi.perTurno[row.key] || { consegne: 0, segnalazioni: 0 };
            return (
              <div
                key={row.key}
                className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-3 ring-1 ring-slate-100/80"
              >
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-slate-800">{row.label}</p>
                  <p className="text-[10px] text-slate-400">{row.fascia}</p>
                </div>
                <div className="text-[11px] text-slate-600 text-right">
                  <span className="font-semibold tabular-nums text-slate-900">{stats.consegne}</span> cons. ·{" "}
                  <span className="font-semibold tabular-nums text-slate-900">{stats.segnalazioni}</span> segn.
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  }

  /* details (default grande) */
  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm" data-testid="adv-card-diario-oggi">
      <CardContent className="p-5">
        {header}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          {DIARIO_TURNO_RIGHE.map((row) => {
            const stats = diarioOggi.perTurno[row.key] || { consegne: 0, segnalazioni: 0 };
            return (
              <div
                key={row.key}
                className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-3 ring-1 ring-slate-100/80"
              >
                <p className="text-[11px] font-bold text-slate-800">{row.label}</p>
                <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">{row.fascia}</p>
                <div className="mt-2 space-y-1 text-[11px] text-slate-600">
                  <p>
                    <span className="font-semibold tabular-nums text-slate-900">{stats.consegne}</span>{" "}
                    {stats.consegne === 1 ? "consegna" : "consegne"}
                  </p>
                  <p>
                    <span className="font-semibold tabular-nums text-slate-900">{stats.segnalazioni}</span>{" "}
                    {stats.segnalazioni === 1 ? "segnalazione" : "segnalazioni"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function OccupazioneVariants({
  size,
  postiLiberiInfo,
  occupazioneData,
  occupazioneChartData,
  occupatePerc,
}) {
  if (size === "kpi") {
    const occ = occupazioneData.find((d) => d.name === "Occupate")?.value ?? 0;
    const lib = occupazioneData.find((d) => d.name === "Libere")?.value ?? 0;
    const kpiLike = {
      key: "occ-kpi",
      label: "Occupazione camere",
      value: `${occupatePerc}%`,
      hint: `${occ} occ. · ${lib} libere`,
      hintTone: "neutral",
      icon: HomeIcon,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
    };
    return <RenderKpiCard kpi={kpiLike} testId="adv-occupazione-kpi" />;
  }

  if (size === "details") {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-slate-900">Occupazione camere</h3>
            <span className="text-[11px] text-slate-500">
              {postiLiberiInfo?.totale_camere ? `${postiLiberiInfo.totale_camere} camere` : "—"}
            </span>
          </div>
          <p className="text-sm font-semibold text-emerald-600 mb-3">{occupatePerc}% posti occupati</p>
          <div className="space-y-2">
            {occupazioneData.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-slate-600 truncate">{item.name}</span>
                </div>
                <span className="text-slate-900 font-semibold flex-shrink-0">{item.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100">
            <Link
              to="/posti-liberi"
              className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1"
            >
              Vedi dettagli camere
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  /* percentage */
  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-bold text-slate-900">Occupazione camere</h3>
          <span className="text-[11px] text-slate-500">
            {postiLiberiInfo?.totale_camere ? `${postiLiberiInfo.totale_camere} camere` : "—"}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative w-40 h-40 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={occupazioneChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={62}
                  outerRadius={74}
                  paddingAngle={occupazioneChartData.length > 1 ? 4 : 0}
                  cornerRadius={8}
                  dataKey="value"
                  stroke="none"
                >
                  {occupazioneChartData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-3xl font-bold text-emerald-600 leading-none">{occupatePerc}%</span>
              <span className="text-[11px] text-slate-500 font-medium mt-0.5">Occupate</span>
            </div>
          </div>
          <div className="flex-1 space-y-2 min-w-0">
            {occupazioneData.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-slate-600 truncate">{item.name}</span>
                </div>
                <span className="text-slate-900 font-semibold flex-shrink-0">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100">
          <Link
            to="/posti-liberi"
            className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1"
          >
            Vedi dettagli camere
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function DeblisteringVariants({
  size,
  deblisteringInfo,
  debTotale,
  debAttivi,
  debPerc,
  debRicavoMensile,
  debPrezzo,
}) {
  const footer = (
    <div className="mt-4 pt-3 border-t border-slate-100">
      <Link to="/deblistering" className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1">
        Vai a Deblistering
        <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );

  if (size === "kpi") {
    const kpiLike = {
      key: "deb-kpi",
      label: "Deblistering",
      value: `${debAttivi} / ${debTotale || "—"}`,
      hint: `${debPerc}% ospiti aderenti`,
      hintTone: "neutral",
      icon: Package,
      iconBg: "bg-cyan-100",
      iconColor: "text-cyan-600",
    };
    return <RenderKpiCard kpi={deblisteringInfo ? kpiLike : null} testId="adv-deb-kpi" />;
  }

  if (size === "details") {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-slate-900">Deblistering</h3>
            <span className="text-[11px] text-slate-500">Questo mese</span>
          </div>
          {deblisteringInfo ? (
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                <span className="font-semibold text-slate-900">{debAttivi}</span> ospiti aderenti su{" "}
                <span className="font-semibold">{debTotale || "—"}</span> ({debPerc}%)
              </p>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${debPerc}%` }} />
              </div>
              <p>
                Ricavo mensile stimato:{" "}
                <strong>
                  € {debRicavoMensile.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              </p>
              <p>
                Prezzo per ospite:{" "}
                <strong>
                  € {debPrezzo.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              </p>
            </div>
          ) : (
            <div className="py-6 text-center">
              <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-500">Deblistering non attivo</p>
            </div>
          )}
          {footer}
        </CardContent>
      </Card>
    );
  }

  /* bar default */
  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Deblistering</h3>
          <span className="text-[11px] text-slate-500">Questo mese</span>
        </div>
        {deblisteringInfo ? (
          <div className="space-y-4">
            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-[11px] text-slate-500 font-medium">Ospiti aderenti</span>
                <span className="text-[11px] font-bold text-emerald-600">{debPerc}%</span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-xl font-bold text-slate-900">
                  {debAttivi} / {debTotale || "—"}
                </span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${debPerc}%` }} />
              </div>
            </div>
            <div className="pt-3 border-t border-slate-100">
              <p className="text-[11px] text-slate-500 font-medium">Ricavo mensile</p>
              <p className="text-lg font-bold text-slate-900 leading-tight">
                € {debRicavoMensile.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 font-medium">Prezzo per ospite</p>
              <p className="text-lg font-bold text-slate-900 leading-tight">
                € {debPrezzo.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        ) : (
          <div className="py-6 text-center">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Deblistering non attivo</p>
          </div>
        )}
        {footer}
      </CardContent>
    </Card>
  );
}

function AttivitaVariants({ size, attivitaRecenti }) {
  const innerList =
    attivitaRecenti.length > 0 ? (
      <div className="space-y-2.5">
        {attivitaRecenti.map((att, idx) => (
          <Link
            key={idx}
            to={att.to}
            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
          >
            <div className={`w-9 h-9 rounded-xl ${att.iconBg} flex items-center justify-center flex-shrink-0`}>
              <att.icon className={`w-[18px] h-[18px] ${att.iconColor}`} strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800 truncate">{att.titolo}</p>
              {att.sottotitolo && <p className="text-xs text-slate-500 truncate">{att.sottotitolo}</p>}
            </div>
            {att.valore && (
              <span className={`text-sm font-bold ${att.valoreColor || "text-slate-700"} flex-shrink-0`}>
                {att.valore}
              </span>
            )}
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
          </Link>
        ))}
      </div>
    ) : (
      <div className="py-8 text-center text-sm text-slate-400">Nessuna attività recente</div>
    );

  const foot = (
    <div className="mt-4 pt-3 border-t border-slate-100">
      <Link
        to="/fascicoli-assistenziali"
        className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1"
      >
        Vedi tutte le attività
        <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );

  if (size === "details") {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
        <CardContent className="p-5">
          <h3 className="text-base font-bold text-slate-900 mb-1">Attività recenti</h3>
          <p className="text-[11px] text-slate-500 mb-4">
            Promemoria operativi: fatture, documenti e avvisi in evidenza.
          </p>
          {innerList}
          {foot}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Attività recenti</h3>
        </div>
        {innerList}
        {foot}
      </CardContent>
    </Card>
  );
}

function UltimiOspitiVariants({ size, ultimiOspiti }) {
  const list =
    ultimiOspiti.length > 0 ? (
      <div className="space-y-2.5">
        {ultimiOspiti.map((o) => (
          <Link
            key={o.id}
            to={`/fascicoli-assistenziali/${o.id}`}
            className="flex items-center gap-3 rounded-xl hover:bg-slate-50 p-1.5 -m-1.5 transition-colors"
          >
            <div
              className={`w-9 h-9 rounded-full ${o.colore} flex items-center justify-center text-xs font-bold flex-shrink-0`}
            >
              {o.iniziali}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800 truncate">{o.nome}</p>
              <p className="text-[11px] text-slate-500 truncate">{o.info}</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] font-semibold px-2 py-0.5 ring-1 ring-emerald-200">
              Attivo
            </Badge>
          </Link>
        ))}
      </div>
    ) : (
      <div className="py-8 text-center">
        <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-xs text-slate-500">Nessun ospite inserito</p>
      </div>
    );

  if (size === "details") {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
        <CardContent className="p-5">
          <h3 className="text-base font-bold text-slate-900 mb-1">Ultimi ospiti inseriti</h3>
          <p className="text-[11px] text-slate-500 mb-4">Ordinati per data di ingresso.</p>
          <div className="flex justify-end mb-3">
            <Link to="/fascicoli-assistenziali" className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700">
              Vedi tutti
            </Link>
          </div>
          {list}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Ultimi ospiti inseriti</h3>
          <Link to="/fascicoli-assistenziali" className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700">
            Vedi tutti
          </Link>
        </div>
        {list}
      </CardContent>
    </Card>
  );
}

function PreIngressiVariants({ size, preIngressiWidget }) {
  const list =
    preIngressiWidget.length > 0 ? (
      <div className="space-y-2.5">
        {preIngressiWidget.map((o) => (
          <Link
            key={o.id}
            to={`/fascicoli-assistenziali/${o.id}`}
            className="block rounded-xl hover:bg-slate-50 p-2.5 -m-1 transition-colors border border-yellow-100/80"
          >
            <p className="text-sm font-semibold text-slate-800 truncate">{o.nome}</p>
            <p className="text-[11px] text-slate-600 mt-0.5">
              Ingresso previsto: <span className="font-medium">{o.ingresso}</span>
            </p>
            <p className="text-[11px] text-slate-600">
              Documentazione: <span className="font-medium">{o.documentazione}</span>
            </p>
            <p className="text-[11px] text-amber-800 font-medium mt-1">
              Stato: {o.stato}
            </p>
          </Link>
        ))}
      </div>
    ) : (
      <div className="py-8 text-center">
        <UserPlus className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-xs text-slate-500">Nessun pre-ingresso in programma</p>
      </div>
    );

  if (size === "details") {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
        <CardContent className="p-5">
          <h3 className="text-base font-bold text-slate-900 mb-1">Pre-ingressi</h3>
          <p className="text-[11px] text-slate-500 mb-4">
            Ospiti registrati con ingresso futuro — fascicolo e documentazione attivi.
          </p>
          {list}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-base h-full bg-white border border-yellow-200/60 shadow-sm bg-yellow-50/20">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Pre-ingressi</h3>
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 text-[10px] font-semibold ring-1 ring-yellow-200">
            {preIngressiWidget.length}
          </Badge>
        </div>
        {list}
      </CardContent>
    </Card>
  );
}

function ScadenzeVariants({ size, scadenzeProssime }) {
  const list =
    scadenzeProssime.length > 0 ? (
      <div className="space-y-2.5">
        {scadenzeProssime.map((s, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl ${s.boxClass} flex flex-col items-center justify-center flex-shrink-0`}>
              <span className="text-sm font-bold leading-none">{s.giorno}</span>
              <span className="text-[9px] font-semibold mt-0.5">{s.mese}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800 truncate">{s.titolo}</p>
              {s.sottotitolo && <p className="text-[11px] text-slate-500 truncate">{s.sottotitolo}</p>}
            </div>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ring-1 flex-shrink-0 ${s.tagClass}`}>
              {s.tag}
            </span>
          </div>
        ))}
      </div>
    ) : (
      <div className="py-8 text-center">
        <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-xs text-slate-500">Nessuna scadenza imminente</p>
      </div>
    );

  if (size === "details") {
    return (
      <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
        <CardContent className="p-5">
          <h3 className="text-base font-bold text-slate-900 mb-1">Scadenze prossime</h3>
          <p className="text-[11px] text-slate-500 mb-4">Piani terapeutici, invii Alloggiati e fatture in scadenza.</p>
          {list}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Scadenze prossime</h3>
        </div>
        {list}
      </CardContent>
    </Card>
  );
}

function QuickActionsCard({ azioniRapide }) {
  return (
    <Card className="card-base h-full bg-white border border-slate-200/70 shadow-sm">
      <CardContent className="p-5">
        <h3 className="text-base font-bold text-slate-900 mb-4">Azioni rapide</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
          {azioniRapide.map((a, idx) => (
            <Link
              key={idx}
              to={a.to}
              className="flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all text-center"
              data-testid={`azione-rapida-${idx}`}
            >
              <div className={`w-10 h-10 rounded-xl ${a.bg} ring-1 ${a.ring} flex items-center justify-center`}>
                <a.icon className={`w-5 h-5 ${a.color}`} />
              </div>
              <span className="text-[11px] font-semibold text-slate-700 leading-tight">{a.label}</span>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

const KPI_PREFIX = "kpi-";

export default function ControlloCardItem({ cardId, size, cardData }) {
  if (!cardData) return null;

  const {
    postiLiberiInfo,
    diarioOggi,
    occupazioneData,
    occupazioneChartData,
    occupatePerc,
    deblisteringInfo,
    debTotale,
    debAttivi,
    debPerc,
    debRicavoMensile,
    debPrezzo,
    attivitaRecenti,
    ultimiOspiti,
    scadenzeProssime,
    preIngressiWidget,
    azioniRapide,
  } = cardData;

  if (cardId.startsWith(KPI_PREFIX)) {
    return <KpiVariants blockId={cardId} size={size} cardData={cardData} />;
  }

  switch (cardId) {
    case "diario-consegne-oggi":
      return <DiarioVariants size={size} diarioOggi={diarioOggi} />;
    case "occupazione-camere":
      return (
        <OccupazioneVariants
          size={size}
          postiLiberiInfo={postiLiberiInfo}
          occupazioneData={occupazioneData}
          occupazioneChartData={occupazioneChartData}
          occupatePerc={occupatePerc}
        />
      );
    case "deblistering":
      return (
        <DeblisteringVariants
          size={size}
          deblisteringInfo={deblisteringInfo}
          debTotale={debTotale}
          debAttivi={debAttivi}
          debPerc={debPerc}
          debRicavoMensile={debRicavoMensile}
          debPrezzo={debPrezzo}
        />
      );
    case "attivita-recenti":
      return <AttivitaVariants size={size} attivitaRecenti={attivitaRecenti} />;
    case "ultimi-ospiti":
      return <UltimiOspitiVariants size={size} ultimiOspiti={ultimiOspiti} />;
    case "pre-ingressi":
      return <PreIngressiVariants size={size} preIngressiWidget={preIngressiWidget || []} />;
    case "scadenze-prossime":
      return <ScadenzeVariants size={size} scadenzeProssime={scadenzeProssime} />;
    case "azioni-rapide":
      return <QuickActionsCard azioniRapide={azioniRapide} />;
    default:
      return null;
  }
}
